import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';

/**
 * Activity Preview Modal Component
 * Shows a realistic preview of how an activity will appear to participants
 */

const ActivityPreviewModal = ({ activity, isOpen, onClose, onEdit, onConfirmAdd }) => {
  const [activeTab, setActiveTab] = useState('preview'); // 'preview' or 'config'
  const [editableQuestion, setEditableQuestion] = useState('');

  // Update editable question when activity changes
  useEffect(() => {
    if (activity && ['challenges', 'actionplans', 'feedback'].includes(activity.type)) {
      setEditableQuestion(activity.question || '');
    }
  }, [activity]);

  if (!isOpen || !activity) return null;

  // Update activity question when it changes
  const handleQuestionChange = (newQuestion) => {
    setEditableQuestion(newQuestion);
    if (activity) {
      activity.question = newQuestion;
    }
  };

  // Render participant-facing preview based on activity type
  const renderParticipantPreview = () => {
    switch (activity.type) {
      case 'poll':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 border-2 border-info rounded-lg flex items-center justify-center bg-white">
                {getActivityIcon('poll')}
              </div>
              <h3 className="text-2xl font-bold text-secondary-900 mb-2">{activity.title}</h3>
              {activity.description && (
                <p className="text-neutral-600">{activity.description}</p>
              )}
            </div>

            <div className="bg-white rounded-lg p-6 border-2 border-neutral-200">
              <h4 className="text-lg font-semibold text-neutral-900 mb-4">
                {activity.config?.question || 'Poll Question'}
              </h4>

              <div className="space-y-3">
                {(activity.config?.options || ['Option 1', 'Option 2', 'Option 3']).map((option, idx) => (
                  <button
                    key={idx}
                    className="w-full p-4 text-left bg-neutral-50 hover:bg-primary-50 border-2 border-neutral-200 hover:border-primary-500 rounded-lg transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full border-2 border-neutral-400 flex items-center justify-center">
                        {activity.config?.allowMultiple ? (
                          <div className="w-4 h-4 rounded border-2 border-neutral-400"></div>
                        ) : (
                          <div className="w-3 h-3 rounded-full bg-neutral-400"></div>
                        )}
                      </div>
                      <span className="text-neutral-900 font-medium">
                        {typeof option === 'string' ? option : option.text}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {activity.config?.allowMultiple && (
                <p className="text-xs text-neutral-500 mt-4">
                  You can select multiple options
                  {activity.config?.maxSelections && ` (up to ${activity.config.maxSelections})`}
                </p>
              )}

              <div className="mt-6 flex justify-between items-center">
                {activity.timeLimit && (
                  <div className="flex items-center gap-2 text-sm text-neutral-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {Math.round(activity.timeLimit / 60)} minutes
                  </div>
                )}
                <Button variant="primary" className="ml-auto">Submit Response</Button>
              </div>
            </div>
          </div>
        );

      case 'quiz':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 border-2 border-success rounded-lg flex items-center justify-center bg-white">
                {getActivityIcon('quiz')}
              </div>
              <h3 className="text-2xl font-bold text-secondary-900 mb-2">{activity.title}</h3>
              {activity.description && (
                <p className="text-neutral-600">{activity.description}</p>
              )}
              <div className="flex items-center justify-center gap-4 mt-4">
                {activity.config?.points && (
                  <Badge variant="warning">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    {activity.config.points} points
                  </Badge>
                )}
                {activity.config?.passingScore && (
                  <Badge variant="info">Passing: {activity.config.passingScore}%</Badge>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border-2 border-neutral-200">
              <h4 className="text-lg font-semibold text-neutral-900 mb-4">
                {activity.config?.question || 'Quiz Question'}
              </h4>

              <div className="space-y-3">
                {(activity.config?.options || [
                  { text: 'Option A', isCorrect: false },
                  { text: 'Option B', isCorrect: true },
                  { text: 'Option C', isCorrect: false },
                  { text: 'Option D', isCorrect: false },
                ]).map((option, idx) => (
                  <button
                    key={idx}
                    className="w-full p-4 text-left bg-neutral-50 hover:bg-green-50 border-2 border-neutral-200 hover:border-green-500 rounded-lg transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full border-2 border-neutral-400 flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-neutral-400"></div>
                      </div>
                      <span className="text-neutral-900 font-medium">{option.text}</span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-6 flex justify-between items-center">
                {activity.config?.timeLimit && (
                  <div className="flex items-center gap-2 text-sm text-neutral-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {activity.config.timeLimit} seconds
                  </div>
                )}
                <Button variant="primary" className="ml-auto">Submit Answer</Button>
              </div>
            </div>
          </div>
        );

      case 'word_cloud':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 border-2 border-purple-600 rounded-lg flex items-center justify-center bg-white">
                {getActivityIcon('word_cloud')}
              </div>
              <h3 className="text-2xl font-bold text-secondary-900 mb-2">{activity.title}</h3>
              {activity.description && (
                <p className="text-neutral-600">{activity.description}</p>
              )}
            </div>

            <div className="bg-white rounded-lg p-6 border-2 border-neutral-200">
              <h4 className="text-lg font-semibold text-neutral-900 mb-4">
                {activity.config?.question || 'What words come to mind?'}
              </h4>

              <div className="mb-6">
                <input
                  type="text"
                  placeholder="Type your words here..."
                  className="w-full px-4 py-3 border-2 border-neutral-300 rounded-lg focus:outline-none focus:border-primary-500"
                />
                <p className="text-xs text-neutral-500 mt-2">
                  {activity.config?.maxWords
                    ? `Submit up to ${activity.config.maxWords} words`
                    : 'Submit your words'}
                </p>
              </div>

              {/* Mock word cloud visualization */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-8 min-h-[200px] flex items-center justify-center">
                <div className="text-center space-y-2">
                  <p className="text-4xl font-bold text-purple-600">Innovation</p>
                  <p className="text-2xl font-semibold text-blue-600">Collaboration</p>
                  <p className="text-3xl font-bold text-pink-600">Growth</p>
                  <p className="text-xl font-medium text-indigo-600">Learning</p>
                  <p className="text-sm text-neutral-400 mt-4">Live word cloud will appear here</p>
                </div>
              </div>

              <div className="mt-6">
                <Button variant="primary" className="w-full">Submit Words</Button>
              </div>
            </div>
          </div>
        );

      case 'open_question':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 border-2 border-warning rounded-lg flex items-center justify-center bg-white">
                {getActivityIcon('open_question')}
              </div>
              <h3 className="text-2xl font-bold text-secondary-900 mb-2">{activity.title}</h3>
              {activity.description && (
                <p className="text-neutral-600">{activity.description}</p>
              )}
            </div>

            <div className="bg-white rounded-lg p-6 border-2 border-neutral-200">
              <h4 className="text-lg font-semibold text-neutral-900 mb-4">
                {activity.config?.question || 'Share your thoughts...'}
              </h4>

              <textarea
                placeholder="Type your response here..."
                rows={6}
                className="w-full px-4 py-3 border-2 border-neutral-300 rounded-lg focus:outline-none focus:border-primary-500 resize-none"
              />

              <div className="mt-2 flex justify-between items-center text-xs text-neutral-500">
                <span>
                  {activity.config?.maxLength
                    ? `Maximum ${activity.config.maxLength} characters`
                    : 'No length limit'}
                </span>
                <span>0 / {activity.config?.maxLength || '∞'}</span>
              </div>

              {activity.config?.allowAnonymous && (
                <label className="flex items-center gap-2 mt-4 text-sm text-neutral-700">
                  <input type="checkbox" className="w-4 h-4 text-primary-600 rounded" />
                  Submit anonymously
                </label>
              )}

              <div className="mt-6">
                <Button variant="primary" className="w-full">Submit Response</Button>
              </div>
            </div>
          </div>
        );

      case 'qna':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 border-2 border-pink-600 rounded-lg flex items-center justify-center bg-white">
                {getActivityIcon('qna')}
              </div>
              <h3 className="text-2xl font-bold text-secondary-900 mb-2">{activity.title}</h3>
              {activity.description && (
                <p className="text-neutral-600">{activity.description}</p>
              )}
            </div>

            <div className="bg-white rounded-lg p-6 border-2 border-neutral-200">
              <h4 className="text-lg font-semibold text-neutral-900 mb-4">Ask a Question</h4>

              <textarea
                placeholder="What would you like to ask?"
                rows={3}
                className="w-full px-4 py-3 border-2 border-neutral-300 rounded-lg focus:outline-none focus:border-primary-500 resize-none"
              />

              {activity.config?.allowAnonymous && (
                <label className="flex items-center gap-2 mt-3 text-sm text-neutral-700">
                  <input type="checkbox" className="w-4 h-4 text-primary-600 rounded" />
                  Ask anonymously
                </label>
              )}

              <Button variant="primary" className="w-full mt-4">Submit Question</Button>

              <div className="mt-6 pt-6 border-t border-neutral-200">
                <h5 className="text-sm font-semibold text-neutral-900 mb-3">Recent Questions</h5>
                <div className="space-y-3">
                  {[
                    'How does this concept apply to real-world scenarios?',
                    'Can you provide more examples?',
                    'What are the best practices?',
                  ].map((question, idx) => (
                    <div key={idx} className="p-3 bg-neutral-50 rounded-lg">
                      <p className="text-sm text-neutral-900 mb-2">{question}</p>
                      <div className="flex items-center gap-3 text-xs text-neutral-500">
                        <span>Anonymous</span>
                        {activity.config?.allowVoting && (
                          <button className="flex items-center gap-1 hover:text-primary-600">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                            {Math.floor(Math.random() * 10)}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'feedback':
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-6 border-2 border-neutral-200">
              <h3 className="text-xl font-bold text-secondary-900 mb-4">Feedback</h3>
              <div className="bg-primary-50 rounded-lg p-4 border border-primary-100">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-semibold text-primary-700">5 Predefined Feedback Questions</span>
                </div>
                <p className="text-xs text-primary-600 mb-4">
                  Feedback activities use standardized questions for consistent metrics. Participants will rate each on a 1-5 emoji scale.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg border border-primary-100">
                    <span className="w-6 h-6 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold text-xs flex-shrink-0">1</span>
                    <div>
                      <p className="text-sm text-neutral-800 font-medium">Topic Relevance (TRS)</p>
                      <p className="text-xs text-neutral-600">How relevant and useful was this topic for your role?</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg border border-primary-100">
                    <span className="w-6 h-6 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold text-xs flex-shrink-0">2</span>
                    <div>
                      <p className="text-sm text-neutral-800 font-medium">Trainer Effectiveness (TEI)</p>
                      <p className="text-xs text-neutral-600">How effectively did the trainer help you understand and engage with this topic?</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg border border-primary-100">
                    <span className="w-6 h-6 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold text-xs flex-shrink-0">3</span>
                    <div>
                      <p className="text-sm text-neutral-800 font-medium">Confidence to Apply (PCS)</p>
                      <p className="text-xs text-neutral-600">How confident do you feel about applying this topic to overcome real work challenges?</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg border border-primary-100">
                    <span className="w-6 h-6 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold text-xs flex-shrink-0">4</span>
                    <div>
                      <p className="text-sm text-neutral-800 font-medium">Learning Advocacy (LAI)</p>
                      <p className="text-xs text-neutral-600">How likely are you to apply this learning and recommend it to others?</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg border border-primary-100">
                    <span className="w-6 h-6 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold text-xs flex-shrink-0">5</span>
                    <div>
                      <p className="text-sm text-neutral-800 font-medium">Challenge Resolution (CRI)</p>
                      <p className="text-xs text-neutral-600">To what extent did this topic address a real challenge you are currently facing?</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'challenges':
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-8 border-2 border-neutral-200">
              <h3 className="text-2xl font-bold text-secondary-900 mb-4">Challenges</h3>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Question</label>
                <textarea
                  value={editableQuestion}
                  onChange={(e) => handleQuestionChange(e.target.value)}
                  placeholder="What challenges are you facing in your current role?"
                  rows={3}
                  className="w-full px-4 py-3 text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                />
              </div>
            </div>
          </div>
        );

      case 'actionplans':
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-8 border-2 border-neutral-200">
              <h3 className="text-2xl font-bold text-secondary-900 mb-4">Action Plans</h3>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Question</label>
                <textarea
                  value={editableQuestion}
                  onChange={(e) => handleQuestionChange(e.target.value)}
                  placeholder="What specific steps will you take to improve?"
                  rows={3}
                  className="w-full px-4 py-3 text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>
            </div>
          </div>
        );

      case 'break':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 border-2 border-neutral-600 rounded-lg flex items-center justify-center bg-white">
                {getActivityIcon('break')}
              </div>
              <h3 className="text-3xl font-bold text-secondary-900 mb-4">{activity.title}</h3>
              {activity.description && (
                <p className="text-lg text-neutral-600 mb-6">{activity.description}</p>
              )}

              <div className="bg-white rounded-lg p-8 border-2 border-neutral-200 max-w-md mx-auto">
                <p className="text-neutral-700 mb-6">
                  {activity.config?.message || "Take a break! We'll resume shortly."}
                </p>

                {activity.config?.showTimer && (
                  <div className="text-center mb-6">
                    <div className="text-6xl font-bold text-primary-600 mb-2">
                      {activity.duration || 10}:00
                    </div>
                    <p className="text-sm text-neutral-600">minutes remaining</p>
                  </div>
                )}

                <div className="space-y-3 text-sm text-neutral-600">
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                    </svg>
                    <p>Stretch and move around</p>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                    <p>Stay hydrated</p>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>Take deep breaths</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 border-2 border-neutral-400 rounded-lg flex items-center justify-center bg-white">
              {getActivityIcon(activity.type)}
            </div>
            <h3 className="text-xl font-semibold text-secondary-900 mb-2">Preview Not Available</h3>
            <p className="text-neutral-600">Preview is not available for this activity type.</p>
          </div>
        );
    }
  };

  // Render configuration details
  const renderConfigDetails = () => {
    if (!activity.config || Object.keys(activity.config).length === 0) {
      return (
        <div className="text-center py-12 text-neutral-500">
          No configuration available
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {Object.entries(activity.config).map(([key, value]) => (
          <div key={key} className="flex justify-between items-start p-4 bg-neutral-50 rounded-lg">
            <div className="flex-1">
              <h5 className="text-sm font-semibold text-secondary-900 mb-1 capitalize">
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </h5>
              <div className="text-sm text-neutral-600">
                {typeof value === 'boolean' ? (
                  <Badge variant={value ? 'success' : 'default'}>{value ? 'Enabled' : 'Disabled'}</Badge>
                ) : typeof value === 'object' && Array.isArray(value) ? (
                  <div className="space-y-1">
                    {value.map((item, idx) => (
                      <div key={idx} className="text-xs">
                        {typeof item === 'object' ? (
                          <span>
                            {item.text}
                            {item.isCorrect && <Badge variant="success" size="sm" className="ml-2">Correct</Badge>}
                          </span>
                        ) : (
                          <span>{item}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : typeof value === 'object' ? (
                  <pre className="text-xs bg-white p-2 rounded border border-neutral-200 overflow-auto">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                ) : (
                  <span>{String(value)}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-neutral-200 bg-white">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`flex-shrink-0 w-14 h-14 rounded-lg flex items-center justify-center border-2 bg-white ${getActivityTypeColor(activity.type)}`}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-secondary-900 mb-2">
                      {activity.title}
                    </h2>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="capitalize">
                        {activity.type?.replace('_', ' ')}
                      </Badge>
                      {activity.timeLimit && (
                        <Badge variant="info">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {Math.round(activity.timeLimit / 60)} min
                        </Badge>
                      )}
                      {activity.required && (
                        <Badge variant="error">Required</Badge>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-neutral-500 hover:text-neutral-700 hover:bg-white rounded-lg transition-colors"
                  title="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Tab Navigation - Hide Configuration tab for simple activities */}
            {!['challenges', 'actionplans', 'feedback'].includes(activity.type) && (
              <div className="px-6 py-3 border-b border-neutral-200 bg-neutral-50">
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTab('preview')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === 'preview'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-white text-neutral-700 hover:bg-neutral-100'
                    }`}
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Participant Preview
                  </button>
                  <button
                    onClick={() => setActiveTab('config')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === 'config'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-white text-neutral-700 hover:bg-neutral-100'
                    }`}
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Configuration
                  </button>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-neutral-50 to-neutral-100">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {activeTab === 'preview' ? renderParticipantPreview() : renderConfigDetails()}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 flex justify-between items-center gap-3">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <div className="flex gap-3">
                {/* Show Save Changes button for simple activities */}
                {['challenges', 'actionplans', 'feedback'].includes(activity.type) ? (
                  <Button variant="primary" onClick={onClose}>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Changes
                  </Button>
                ) : (
                  <>
                    {onEdit && (
                      <Button variant="secondary" onClick={() => onEdit(activity)}>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit Before Adding
                      </Button>
                    )}
                    <Button variant="primary" onClick={() => onConfirmAdd(activity)}>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add to Session
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ActivityPreviewModal;
